const DELIMITER_TO_API = {
  ",": "comma",
  ";": "l",
  "|": "l",
  "\t": "tab",
  comma: "comma",
  l: "l",
  tab: "tab",
};

const DELIMITER_FROM_API = {
  comma: ",",
  l: ";",
  tab: "\t",
};

const QUALIFIER_TO_API = {
  '"': "double_quotes",
  "'": "quotation",
  none: "empty",
  double_quotes: "double_quotes",
  quotation: "quotation",
  empty: "empty",
};

const QUALIFIER_FROM_API = {
  double_quotes: '"',
  quotation: "'",
  empty: "none",
};

const DEFAULT_FORM_DATA = {
  schedule_name: "",
  schedule_status: "active",
  export_status: "inactive",
  export_description: "",
  file_name: "",
  vendor: "",
  include_header_row: true,
  field_delimiter: ",",
  text_qualifier: '"',
  destination_description: "",
  protocol: "SFTP",
  host: "",
  port: "22",
  user: "",
  password: "",
  directory: "",
};

export const DEFAULT_SCHEDULE_REPORT = {
  frequency: "one_time_only",
  custom_minutes: "30",
  custom_hours: "1",
  schedule_date: "",
  schedule_time: "",
  timezone: "GMT-8",
  day_of_month: "1",
};

export const SCHEDULE_TIME_INTERVAL_MINUTES = 5;

export const snapScheduleTimeToFiveMinutes = (timeStr) => {
  if (!timeStr) return "";
  const match = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return timeStr;

  let hour = Number.parseInt(match[1], 10);
  let minute = Number.parseInt(match[2], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return timeStr;

  minute = Math.round(minute / SCHEDULE_TIME_INTERVAL_MINUTES) * SCHEDULE_TIME_INTERVAL_MINUTES;
  if (minute === 60) {
    minute = 0;
    hour = (hour + 1) % 24;
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const buildScheduleTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += SCHEDULE_TIME_INTERVAL_MINUTES) {
      const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      options.push({ value, label: value });
    }
  }
  return options;
};

const SCHEDULE_TIME_OPTIONS = buildScheduleTimeOptions();

export const getScheduleTimeOption = (timeStr) => {
  const snapped = snapScheduleTimeToFiveMinutes(timeStr);
  if (!snapped) return null;
  return (
    SCHEDULE_TIME_OPTIONS.find((option) => option.value === snapped) || {
      value: snapped,
      label: snapped,
    }
  );
};

export const parseScheduleFromApi = (schedule) => {
  if (!schedule) {
    return {
      id: null,
      formData: { ...DEFAULT_FORM_DATA },
      scheduleReport: { ...DEFAULT_SCHEDULE_REPORT },
      recipients: [],
      runInfo: {
        next_run_at: null,
        last_run_at: null,
        last_run_status: null,
        last_run_message: "",
        run_history: [],
      },
    };
  }

  const scheduleReport =
    typeof schedule.schedule_report === "string"
      ? (() => {
          try {
            return JSON.parse(schedule.schedule_report || "{}");
          } catch {
            return {};
          }
        })()
      : schedule.schedule_report || {};

  return {
    id: schedule.id || null,
    formData: {
      schedule_name: schedule.name || "",
      schedule_status: schedule.schedule_status ? "active" : "inactive",
      export_status: schedule.export_status ? "active" : "inactive",
      export_description: schedule.export_description || "",
      file_name: schedule.export_file_name || "",
      vendor:
        schedule.vendor && typeof schedule.vendor === "object"
          ? schedule.vendor.id
          : schedule.vendor || "",
      include_header_row: scheduleReport.include_header_row !== false,
      field_delimiter: DELIMITER_FROM_API[schedule.field_delimiter] || ",",
      text_qualifier: QUALIFIER_FROM_API[schedule.text_qualifier] || '"',
      destination_description: schedule.destination_description || "",
      protocol: (schedule.protocol || "sftp").toUpperCase(),
      host: schedule.host || "",
      port: schedule.port || "22",
      user: schedule.user || "",
      password: "",
      directory: schedule.directory || "",
    },
    scheduleReport: {
      frequency: scheduleReport.frequency || DEFAULT_SCHEDULE_REPORT.frequency,
      custom_minutes: scheduleReport.custom_minutes || DEFAULT_SCHEDULE_REPORT.custom_minutes,
      custom_hours: scheduleReport.custom_hours || DEFAULT_SCHEDULE_REPORT.custom_hours,
      schedule_date: scheduleReport.schedule_date || "",
      schedule_time: snapScheduleTimeToFiveMinutes(scheduleReport.schedule_time || ""),
      timezone: scheduleReport.timezone || DEFAULT_SCHEDULE_REPORT.timezone,
      day_of_month: scheduleReport.day_of_month || DEFAULT_SCHEDULE_REPORT.day_of_month,
    },
    runInfo: {
      next_run_at: schedule.next_run_at || null,
      last_run_at: schedule.last_run_at || null,
      last_run_status: schedule.last_run_status || null,
      last_run_message: schedule.last_run_message || "",
      run_history: Array.isArray(schedule.run_history) ? schedule.run_history : [],
    },
    recipients: Array.isArray(schedule.recipients) ? schedule.recipients : [],
  };
};

export const buildSchedulePayload = ({
  formData,
  scheduleReport,
  recipients = [],
  scheduleFilters = [],
  lockFilters = false,
  id = null,
}) => {
  const resolvedScheduleFilters = lockFilters ? [] : scheduleFilters;
  const payload = {
    name: formData.schedule_name,
    schedule_status: formData.schedule_status === "active",
    export_status: formData.export_status === "active",
    export_description: formData.export_description,
    export_file_name: formData.file_name,
    field_delimiter: DELIMITER_TO_API[formData.field_delimiter] || "comma",
    text_qualifier: QUALIFIER_TO_API[formData.text_qualifier] || "double_quotes",
    destination_description: formData.destination_description,
    protocol: (formData.protocol || "SFTP").toLowerCase(),
    host: formData.host,
    port: String(formData.port || "22"),
    user: formData.user,
    directory: formData.directory,
    schedule_report: {
      ...scheduleReport,
      schedule_time: snapScheduleTimeToFiveMinutes(scheduleReport.schedule_time),
      include_header_row: !!formData.include_header_row,
      filters: resolvedScheduleFilters.map((filter) => ({
        id: filter.id,
        field_name: filter.field_name,
        operator: filter.operator,
        value: filter.value,
        table_name: filter.table_name,
        label: filter.label,
        type: filter.type,
        is_active: filter.is_active,
        ...(filter.enumValues ? { enum_values: filter.enumValues } : {}),
      })),
    },
    recipients,
  };

  if (formData.password) {
    payload.password = formData.password;
  }

  if (id) {
    payload.id = id;
  }

  return payload;
};

const getTimezoneOffsetHours = (timezone = "GMT-8") => {
  const match = String(timezone).match(/GMT([+-])(\d+)/i);
  if (!match) return -8;
  return (match[1] === "+" ? 1 : -1) * Number.parseInt(match[2], 10);
};

export const toLocalDayKey = (dateValue, offsetHours) => {
  const date = new Date(dateValue);
  const shifted = new Date(date.getTime() + offsetHours * 60 * 60 * 1000);
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}-${String(shifted.getUTCDate()).padStart(2, "0")}`;
};

export const getScheduleTimezoneOffsetHours = getTimezoneOffsetHours;

export const scheduleRanToday = (runHistory = [], timezone = "GMT-8") => {
  if (!Array.isArray(runHistory) || runHistory.length === 0) return false;
  const todayKey = toLocalDayKey(new Date(), getTimezoneOffsetHours(timezone));
  return runHistory.some(
    (entry) =>
      entry?.status === "success" &&
      entry?.ran_at &&
      toLocalDayKey(entry.ran_at, getTimezoneOffsetHours(timezone)) === todayKey
  );
};
